import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  Bell,
  CloudSun,
  DotsThreeOutline,
  Link as LinkIcon,
  MagnifyingGlass,
  Sparkle,
  X,
} from '@phosphor-icons/react';

import { GlobalSearch } from './GlobalSearch';
import { HeaderProfileMenu } from './HeaderProfileMenu';
import { CustomerLinkShare } from './CustomerLinkShare';
import { NotificationDropdown } from '../NotificationDropdown';
import { RutaAiTrigger } from '../dashboard/live-ops/RutaAiTrigger';
import { PageHelpButton } from '../onboarding/PageHelpButton';
import { MiniPipeline } from '@/components/dashboard/live-ops/MiniPipeline';
import { usePipelineStages } from '@/hooks/usePipelineStages';
import { useRutaAiAccess } from '@/hooks/useRutaAiAccess';
import { useRutaAi } from '@/contexts/RutaAiContext';
import { WeatherWidget } from '@/components/ui/weather-widget';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNotifications, useOrganization } from '@/hooks/useQueries';
import { useHiddenInAppNotificationTypes } from '@/hooks/useHiddenInAppNotificationTypes';
import { getFullCustomerPortalUrl } from '@/lib/constants';
import { NotificationType } from '@ruta/shared';
import { cn } from '@/lib/utils';
import { ASSISTANT_BRAND } from '@/components/ai-elements/assistant-brand';
import type { PipelineStage } from '@/components/dashboard/live-ops/LifecyclePipeline';

/**
 * Global utilities overlaid on the page-header top row (AppChromeHeader).
 *
 * Desktop (md+), left → right:
 *   [ Search ]  [ Work queue ]  [ Weather ]  [ Links · AI · Bell ]  [ Avatar ]
 *
 * Mobile (< md): a single menu button. Items are labeled anchors —
 *   Search opens a fixed panel; Work queue stages jump to their lists;
 *   Notifications / Weather / Share / Ask open their existing surfaces.
 *   Avatar stays beside the menu.
 *
 * Mounted outside AnimatedOutlet so this cluster does not unmount on route changes.
 */
export function HeaderUtilityCluster() {
  const canUseRutaAi = useRutaAiAccess();
  const { data: orgData } = useOrganization();
  const slug = orgData?.organization?.slug || orgData?.organization?.orgId;
  const customerPortalUrl = slug ? getFullCustomerPortalUrl(slug) : '';
  const { stages: pipelineStages, loading: pipelineLoading } = usePipelineStages();

  return (
    <>
      {/* ── Mobile: menu + avatar ───────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 md:hidden">
        <MobileToolsMenu
          canUseRutaAi={canUseRutaAi}
          customerPortalUrl={customerPortalUrl}
          pipelineStages={pipelineStages}
          pipelineLoading={pipelineLoading}
        />
        <HeaderProfileMenu />
      </div>

      {/* ── Desktop: full utility strip ─────────────────────────────────── */}
      {/* The data-tour anchors are what the first-run tour numbers on its top-bar stop. */}
      <div className="hidden md:flex items-center gap-3" data-tour="header-tools">
        <div
          className="h-9 w-40 min-w-0 shrink lg:w-56 xl:w-64"
          data-tour="header-search"
        >
          <GlobalSearch variant="embedded" className="h-9" popoverAlign="end" />
        </div>
        <MiniPipeline loading={pipelineLoading} stages={pipelineStages} />

        <div className="flex items-center gap-1.5 pl-1">
          <div className="flex items-center" data-tour="header-weather">
            <WeatherWidget
              variant="inline"
              showLocation={false}
              className="text-white/80 hover:text-white"
            />
          </div>

          <div className="flex items-center" data-tour="header-links">
            <CustomerLinkShare
              customerPortalUrl={customerPortalUrl}
              disabled={!customerPortalUrl}
            />
          </div>
          {canUseRutaAi && (
            <div className="flex items-center" data-tour="header-ai">
              <RutaAiTrigger />
            </div>
          )}
          <PageHelpButton />
          <div className="flex items-center" data-tour="header-bell">
            <NotificationDropdown />
          </div>

          <div className="ml-1.5" data-tour="header-profile">
            <HeaderProfileMenu />
          </div>
        </div>
      </div>
    </>
  );
}

type MobileSurface = 'search' | 'notifications' | 'weather' | 'share' | null;

/**
 * One menu trigger. Each item opens an existing tool surface (search panel,
 * work-queue stage, notifications dropdown, weather modal, share menu,
 * Ask / Ruta AI detached panel).
 */
function MobileToolsMenu({
  canUseRutaAi,
  customerPortalUrl,
  pipelineStages,
  pipelineLoading,
}: {
  canUseRutaAi: boolean;
  customerPortalUrl: string;
  pipelineStages: PipelineStage[];
  pipelineLoading: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [surface, setSurface] = useState<MobileSurface>(null);
  const location = useLocation();
  const { openAi, detachAi } = useRutaAi();

  // Unread badge on the menu trigger (same filter as NotificationDropdown).
  const hiddenTypes = useHiddenInAppNotificationTypes();
  const { data: notifData } = useNotifications(25, true, false, hiddenTypes);
  const allNotifs = notifData?.pages.flatMap((p) => p?.notifications ?? []) ?? [];
  const customerMessageUnread = allNotifs.filter(
    (n) => n?.type === NotificationType.CUSTOMER_MESSAGE && !n.read,
  ).length;
  const unreadCount = Math.max(0, (notifData?.pages[0]?.unreadCount || 0) - customerMessageUnread);
  const hasUnread = unreadCount > 0;

  // Work-queue items waiting — same counts as the desktop MiniPipeline.
  const workQueueTotal = pipelineStages.reduce((sum, s) => sum + (s.count ?? 0), 0);
  const hasUrgentWork = pipelineStages.some((s) => s.urgent && (s.count ?? 0) > 0);
  const menuBadgeCount = unreadCount + workQueueTotal;
  const showMenuBadge = menuBadgeCount > 0;

  // Close search after navigating from a result.
  useEffect(() => {
    setSurface((s) => (s === 'search' ? null : s));
  }, [location.pathname, location.search]);

  // Lock body scroll only for the full-screen-ish search panel.
  useEffect(() => {
    if (surface !== 'search') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [surface]);

  useEffect(() => {
    if (surface !== 'search') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSurface(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [surface]);

  const openSurface = (next: MobileSurface) => {
    // Defer so the menu can finish closing before another layer mounts.
    setTimeout(() => setSurface(next), 0);
  };

  const openRutaAi = () => {
    // Detached panel doesn't need the header sparkle as an anchor.
    openAi(undefined, { framing: 'ask' });
    detachAi();
  };

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={
              showMenuBadge
                ? `Menu, ${menuBadgeCount} items need attention`
                : 'Menu'
            }
            aria-haspopup="menu"
            className={cn(
              'relative inline-flex h-9 w-9 items-center justify-center rounded-md',
              'text-white/85 transition-colors',
              'hover:bg-white/10 hover:text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
              'data-[state=open]:bg-white/10 data-[state=open]:text-white',
            )}
          >
            <DotsThreeOutline className="h-5 w-5" weight="fill" />
            {showMenuBadge && (
              <span
                className={cn(
                  'absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold',
                  hasUrgentWork || hasUnread
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-white text-stone-900',
                )}
              >
                {menuBadgeCount.toLocaleString()}
              </span>
            )}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" sideOffset={8} className="w-60 p-1.5 max-h-[min(70dvh,32rem)] overflow-y-auto">
          <DropdownMenuLabel className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Work queue
          </DropdownMenuLabel>

          {pipelineStages.map((stage) => {
            const Icon = stage.icon;
            const count = stage.count;
            const pending = pipelineLoading || count === undefined;
            const hasWork = (count ?? 0) > 0;
            const isUrgent = !!stage.urgent && hasWork;
            return (
              <DropdownMenuItem
                key={stage.id}
                className="cursor-pointer gap-2.5 rounded-md px-2 py-2.5"
                onSelect={() => stage.navigate()}
              >
                <Icon
                  className={cn(
                    'h-4 w-4 shrink-0',
                    isUrgent ? 'text-red-500' : hasWork ? 'text-stone-700' : 'text-stone-400',
                  )}
                  strokeWidth={2}
                />
                <span className={cn('flex-1 font-medium', !hasWork && !pending && 'text-stone-500')}>
                  {stage.title}
                </span>
                {pending ? (
                  <span className="text-xs text-stone-400">…</span>
                ) : hasWork ? (
                  <span
                    className={cn(
                      'min-w-[1.25rem] rounded-full px-1.5 py-0.5 text-center text-[10px] font-semibold tabular-nums',
                      isUrgent
                        ? 'bg-destructive text-destructive-foreground'
                        : 'bg-stone-200 text-stone-800',
                    )}
                  >
                    {count!.toLocaleString()}
                  </span>
                ) : null}
              </DropdownMenuItem>
            );
          })}

          <DropdownMenuSeparator />

          <DropdownMenuLabel className="px-2 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Tools
          </DropdownMenuLabel>

          <DropdownMenuItem
            className="cursor-pointer gap-2.5 rounded-md px-2 py-2.5"
            onSelect={() => openSurface('search')}
          >
            <MagnifyingGlass className="h-4 w-4 shrink-0 text-stone-500" weight="bold" />
            <span className="font-medium">Search</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer gap-2.5 rounded-md px-2 py-2.5"
            onSelect={() => openSurface('notifications')}
          >
            <Bell className="h-4 w-4 shrink-0 text-stone-500" weight="bold" />
            <span className="flex-1 font-medium">Notifications</span>
            {hasUnread && (
              <span className="rounded-full bg-destructive px-1.5 py-0.5 text-[10px] font-semibold text-destructive-foreground">
                {unreadCount}
              </span>
            )}
          </DropdownMenuItem>

          {canUseRutaAi && (
            <DropdownMenuItem
              className="cursor-pointer gap-2.5 rounded-md px-2 py-2.5"
              onSelect={() => openRutaAi()}
            >
              <Sparkle
                className="h-4 w-4 shrink-0"
                weight="fill"
                style={{ color: ASSISTANT_BRAND.sage }}
              />
              <span className="font-medium">Ask</span>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            className="cursor-pointer gap-2.5 rounded-md px-2 py-2.5"
            onSelect={() => openSurface('weather')}
          >
            <CloudSun className="h-4 w-4 shrink-0 text-stone-500" weight="bold" />
            <span className="font-medium">Weather</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer gap-2.5 rounded-md px-2 py-2.5"
            disabled={!customerPortalUrl}
            onSelect={() => openSurface('share')}
          >
            <LinkIcon className="h-4 w-4 shrink-0 text-stone-500" weight="bold" />
            <span className="font-medium">Share links</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Anchors for menu items — no visible triggers; opened via `surface`. */}
      <NotificationDropdown
        hideTrigger
        open={surface === 'notifications'}
        onOpenChange={(open) => {
          if (!open) setSurface(null);
        }}
      />
      <CustomerLinkShare
        hideTrigger
        customerPortalUrl={customerPortalUrl}
        disabled={!customerPortalUrl}
        open={surface === 'share'}
        onOpenChange={(open) => {
          if (!open) setSurface(null);
        }}
      />
      <WeatherWidget
        hideTrigger
        variant="inline"
        showLocation={false}
        detailsOpen={surface === 'weather'}
        onDetailsOpenChange={(open) => {
          if (!open) setSurface(null);
        }}
      />

      {surface === 'search' &&
        createPortal(
          <>
            <button
              type="button"
              aria-label="Close search"
              className="fixed inset-0 z-[100] bg-black/50"
              onClick={() => setSurface(null)}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Search"
              className="fixed z-[101] left-2 right-2 top-[3.75rem] flex max-h-[min(75dvh,calc(100dvh-4.5rem))] flex-col overflow-hidden rounded-xl border border-stone-200 bg-white shadow-2xl"
            >
              <div className="flex items-center justify-between border-b border-stone-100 bg-stone-50 px-3 py-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">
                  Search
                </span>
                <button
                  type="button"
                  onClick={() => setSurface(null)}
                  aria-label="Close"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-500 hover:bg-stone-200/70 hover:text-stone-800"
                >
                  <X className="h-4 w-4" weight="bold" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                <GlobalSearch
                  variant="panel"
                  className="h-full min-h-[12rem]"
                  onNavigate={() => setSurface(null)}
                />
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
